import React, { useEffect } from 'react';

import PropTypes from 'prop-types';

import { Box, Stack, Typography } from '@mui/material';

import { CheckCircle2 } from 'lucide-react';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import Reveal from '../../Components/Reveal';

import { COLORS } from '../../Constant/COLORS';

// Shared premium layout for informational / legal pages (Careers, Privacy, etc.).
// Renders a teal hero + a readable content column of titled sections.
const StaticPageLayout = ({ eyebrow, title, intro, updatedAt, sections, children }) => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <>
      <Header />

      <Box
        sx={{
          background: `linear-gradient(160deg, ${COLORS.tealDark} 0%, ${COLORS.teal} 100%)`,
          color: '#fff',
        }}>
        <Box maxWidth={{ xs: '90%', md: 900 }} mx='auto' py={{ xs: 6, md: 9 }}>
          <Reveal>
            {eyebrow && (
              <Typography
                variant='overline'
                sx={{ color: 'rgba(255,255,255,0.82)', letterSpacing: '2.5px', display: 'block' }}>
                {eyebrow}
              </Typography>
            )}
            <Typography variant='h1' sx={{ color: '#fff', mt: 1 }}>
              {title}
            </Typography>
            {intro && (
              <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 2.5, maxWidth: 640, lineHeight: 1.7 }}>
                {intro}
              </Typography>
            )}
            {updatedAt && (
              <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.75)', mt: 2.5, display: 'block' }}>
                Last updated: {updatedAt}
              </Typography>
            )}
          </Reveal>
        </Box>
      </Box>

      <Box maxWidth={{ xs: '90%', md: 820 }} mx='auto' py={{ xs: 5, md: 8 }}>
        {sections.map((section, index) => (
          <Reveal key={section.heading || index} delay={Math.min(index, 6) * 0.04}>
            <Box mb={{ xs: 4, md: 5 }}>
              {section.heading && (
                <Typography variant='h4' mb={2}>
                  {section.heading}
                </Typography>
              )}
              {(section.paragraphs || []).map((paragraph, i) => (
                <Typography key={i} color={COLORS.ink} mb={2} sx={{ lineHeight: 1.85 }}>
                  {paragraph}
                </Typography>
              ))}
              {section.bullets && (
                <Stack component='ul' spacing={1.25} sx={{ listStyle: 'none', pl: 0, m: 0, mt: 1 }}>
                  {section.bullets.map((bullet, i) => (
                    <Stack
                      key={i}
                      component='li'
                      direction='row'
                      gap={1.25}
                      alignItems='flex-start'>
                      <CheckCircle2
                        size={20}
                        color={COLORS.teal}
                        strokeWidth={1.8}
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Typography color={COLORS.ink} sx={{ lineHeight: 1.7 }}>
                        {bullet}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Reveal>
        ))}
        {children}
      </Box>

      <Footer />
    </>
  );
};

StaticPageLayout.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  intro: PropTypes.string,
  updatedAt: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      paragraphs: PropTypes.arrayOf(PropTypes.string),
      bullets: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  children: PropTypes.node,
};

StaticPageLayout.defaultProps = { sections: [] };

export default StaticPageLayout;
